import { useState } from "react";
import { ArrowLeft, Bell, BellOff, Search, Grid2x2, List, ChevronDown, Send, UserPlus, Clock, Bluetooth, Thermometer } from "lucide-react";

type Status = "critical" | "warning" | "normal" | "noscan";

type Patient = {
  id: string;
  ref: string;
  status: Status;
  temp: string; // °C
  bars: number[];
  updated: string;
  ago: string;
  bp: string;
  hr: string;
  hrv: string;
  rr: string;
  rrAlert?: boolean;
  alerts: number;
};

// Deterministic 26-bar histogram. `seed` keeps each patient's pattern stable
// across renders; `profile` biases the baseline so critical cases visibly
// trend higher than normal ones without overlapping the [0.15, 1] envelope.
const bars = (seed: number, profile: "hi" | "mid" | "low" = "mid") =>
  Array.from({ length: 26 }, (_, i) => {
    const base = profile === "hi" ? 0.75 : profile === "low" ? 0.4 : 0.6;
    return Math.min(1, Math.max(0.15, base + Math.sin(i / 1.3 + seed) * 0.22 + Math.cos(i / 2.1 + seed) * 0.12));
  });


const PATIENTS: Patient[] = [
  { id: "440363", ref: "2007B", status: "critical", temp: "38.7", bars: bars(1, "hi"), updated: "30/10/23 15:40", ago: "27h 46m", bp: "118/82", hr: "71", hrv: "58", rr: "13", alerts: 3 },
  { id: "440267", ref: "2007C", status: "critical", temp: "38.7", bars: bars(2, "hi"), updated: "30/10/23 15:40", ago: "20h 16m", bp: "110/74", hr: "60", hrv: "83", rr: "9", rrAlert: true, alerts: 24 },
  { id: "440263", ref: "2005", status: "critical", temp: "38.4", bars: bars(3, "hi"), updated: "30/10/23 15:40", ago: "18h 02m", bp: "128/65", hr: "68", hrv: "60", rr: "12", alerts: 2 },
  { id: "440410", ref: "2005A", status: "critical", temp: "38.4", bars: bars(4, "hi"), updated: "30/10/23 15:40", ago: "39h 14m", bp: "130/85", hr: "83", hrv: "95", rr: "9", rrAlert: true, alerts: 14 },
  { id: "440271", ref: "2010A", status: "warning", temp: "37.6", bars: bars(5, "mid"), updated: "30/10/23 15:40", ago: "29h 30m", bp: "104/75", hr: "81", hrv: "67", rr: "15", alerts: 0 },
  { id: "440249", ref: "2011", status: "warning", temp: "37.1", bars: bars(6, "mid"), updated: "30/10/23 15:40", ago: "12h 10m", bp: "127/85", hr: "60", hrv: "72", rr: "14", alerts: 0 },
  { id: "440480", ref: "2002b", status: "noscan", temp: "36.9", bars: bars(7, "low"), updated: "30/10/23 15:40", ago: "33h 10m", bp: "-", hr: "-", hrv: "-", rr: "-", alerts: 0 },
  { id: "440470", ref: "2004B", status: "critical", temp: "36.6", bars: bars(8, "hi"), updated: "30/10/23 15:40", ago: "39h 16m", bp: "134/82", hr: "45", hrv: "59", rr: "9", rrAlert: true, alerts: 14 },
  { id: "440471", ref: "CAR", status: "normal", temp: "36.4", bars: bars(9, "low"), updated: "30/10/23 15:40", ago: "2h 04m", bp: "120/78", hr: "66", hrv: "70", rr: "14", alerts: 0 },
];

// Status → Tailwind class maps. Centralised so the card, histogram and detail
// panel stay visually consistent when a patient's triage status changes.
const BORDER: Record<Status, string> = {
  critical: "border-destructive/70 ring-1 ring-destructive/30",
  warning: "border-[oklch(0.78_0.16_80)]/60",
  normal: "border-success/40",
  noscan: "border-white/10 opacity-90",
};
const TEMP_COLOR: Record<Status, string> = {
  // critical uses amber (not red) so the number stays legible on the red border
  critical: "text-[oklch(0.78_0.16_80)]",
  warning: "text-success",
  normal: "text-success",
  noscan: "text-muted-foreground",
};
const BAR_FROM: Record<Status, string> = {
  critical: "from-destructive via-[oklch(0.78_0.16_80)] to-[oklch(0.78_0.16_80)]",
  warning: "from-success via-[oklch(0.78_0.16_80)] to-[oklch(0.78_0.16_80)]",
  normal: "from-success via-success to-success",
  noscan: "from-white/15 via-white/15 to-white/15",
};


function Bars({ data, status }: { data: number[]; status: Status }) {
  return (
    <div className="flex h-10 items-end gap-[2px]">
      {data.map((v, i) => (
        <div key={i} className={`w-full rounded-sm bg-gradient-to-t ${BAR_FROM[status]}`} style={{ height: `${v * 100}%` }} />
      ))}
    </div>
  );
}

function Card({ p, onSelect, active }: { p: Patient; onSelect: () => void; active: boolean }) {
  return (
    <button
      onClick={onSelect}
      aria-pressed={active}
      className={`group relative w-full overflow-hidden rounded-2xl border bg-black/50 p-3.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:bg-black/70 hover:shadow-[0_10px_30px_-12px_oklch(0_0_0/0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${BORDER[p.status]} ${active ? "ring-2 ring-primary/70 ring-offset-2 ring-offset-background" : ""}`}
    >
      <div className="flex items-center justify-between text-xs">
        <div className="min-w-0 truncate font-mono tracking-wider">
          <span className="text-foreground/90">{p.id}</span>
          <span className="text-muted-foreground"> / {p.ref}</span>
        </div>
        <div className="relative shrink-0">
          <Bell className={`h-3.5 w-3.5 ${p.alerts > 0 ? "text-destructive" : "text-muted-foreground"}`} />
          {p.alerts > 0 && (
            <span className="absolute -right-2 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground shadow">
              {p.alerts}
            </span>
          )}
        </div>
      </div>

      {p.status === "noscan" ? (
        <div className="my-7 grid place-items-center gap-1 text-center">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground">
            <Thermometer className="h-3.5 w-3.5" />
          </div>
          <div className="text-xs text-muted-foreground">No scan</div>
        </div>
      ) : (
        <>
          <div className="mt-2.5 text-[10px] uppercase tracking-wider text-muted-foreground">Temp (°C)</div>
          <div className="mt-0.5 flex items-end gap-3">
            <div className={`text-3xl font-light leading-none tabular-nums ${TEMP_COLOR[p.status]}`}>{p.temp}</div>
            <div className="min-w-0 flex-1"><Bars data={p.bars} status={p.status} /></div>
          </div>
        </>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="truncate">Mis à jour, {p.updated}</span>
        <span className="ml-2 inline-flex shrink-0 items-center gap-1"><Clock className="h-2.5 w-2.5" />{p.ago}</span>
      </div>

      {p.status !== "noscan" && (
        <div className="mt-3 grid grid-cols-4 gap-2 border-t border-white/5 pt-2.5">
          {[
            ["BP (mmHg)", p.bp],
            ["HR (bpm)", p.hr],
            ["HRV (ms)", p.hrv],
            ["RR (bpm)", p.rr],
          ].map(([l, v], i) => (
            <div key={l} className="min-w-0">
              <div className="truncate text-[9px] uppercase tracking-wide text-muted-foreground">{l}</div>
              <div className={"text-sm font-light tabular-nums " + (i === 3 && p.rrAlert ? "text-destructive" : "text-foreground")}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}


function DetailPanel({ p }: { p: Patient }) {
  return (
    <div className="sticky top-4 w-full rounded-[28px] border border-white/10 bg-black p-4 shadow-2xl">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>4:40</span>
        <span className="font-medium text-primary">VITALTRAQ</span>
        <Bluetooth className="h-3 w-3" />
      </div>

      <div className="mt-4 rounded-2xl bg-white/[0.03] p-3">
        <div className="flex items-center gap-2 text-xs">
          <Thermometer className="h-3.5 w-3.5 text-destructive" /> Temperature
        </div>
        <div className="text-[10px] text-muted-foreground">Oral Equivalent Temperature</div>
        <div className="mt-2 flex items-end gap-3">
          {/* Card list shows °C; the VitalTraq mock mirrors a US device that reports °F. */}
          <div className="text-3xl font-light text-destructive">{(parseFloat(p.temp) * 9 / 5 + 32).toFixed(1)} °F</div>

          <div className="min-w-0 flex-1"><Bars data={p.bars} status={p.status} /></div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Bell className="h-2.5 w-2.5" /> 100.4 °F</span>
          <span className="inline-flex items-center gap-1"><Bluetooth className="h-2.5 w-2.5" /> 4:36 PM</span>
        </div>
      </div>

      <div className="mt-3 rounded-2xl bg-destructive/20 p-3 text-xs">
        <div className="font-medium">Temperature Alert</div>
        <div className="mt-1 text-[10px] text-foreground/80">Your temperature is at or above the threshold.</div>
      </div>

      <div className="mt-3 rounded-2xl bg-white/[0.03] p-3">
        <div className="text-xs font-medium">Latest Scan Result</div>
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Clock className="h-2.5 w-2.5" /> {p.updated}</div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          {[
            ["BP (mmHg)", p.bp],
            ["HR (bpm)", p.hr],
            ["HRV (ms)", p.hrv],
            ["RR (bpm)", p.rr],
          ].map(([l, v]) => (
            <div key={l}>
              <div className="text-[9px] text-muted-foreground">{l}</div>
              <div className="text-base font-light text-primary">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 text-[10px] text-muted-foreground">Next scan should be performed at {p.updated}</div>
      <button className="mt-2 w-full rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground">Scan Now</button>
    </div>
  );
}

export default function VitalSignsGridView({ onBack }: { onBack: () => void }) {
  const [filter, setFilter] = useState<"ALL" | "ACTIVE">("ALL");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [muted, setMuted] = useState(false);
  const [selected, setSelected] = useState<Patient>(PATIENTS[0]);
  const alerts = PATIENTS.filter((p) => p.alerts > 0);

  return (
    <div className="space-y-4">
      <div className="card-surface grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl px-3 py-3 sm:px-4 lg:flex lg:flex-wrap">
        <button onClick={onBack} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground" aria-label="Retour">
          <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Retour</span>
        </button>
        <div className="flex shrink-0 items-baseline gap-2">
          <h2 className="text-xl font-light">Patients</h2>
          <span className="text-xs text-muted-foreground">({PATIENTS.length})</span>
        </div>
        <div className="relative col-span-2 min-w-0 flex-1 lg:col-span-1 lg:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input placeholder="Rechercher un patient…" className="h-9 w-full rounded-full border border-white/10 bg-black/30 pl-9 pr-3 text-xs outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/60 focus:bg-black/50" />
        </div>
        <button className="hidden shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground md:inline-flex">
          Température, décroissant <ChevronDown className="h-3 w-3" />
        </button>
        <div className="col-span-2 flex flex-wrap items-center gap-2 lg:col-span-1 lg:ml-auto">
          <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs">
            {(["ALL", "ACTIVE"] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={"rounded-full px-3 py-1 transition-colors " + (filter === f ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}>{f}</button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-white/10 p-0.5">
            <button onClick={() => setView("grid")} aria-label="Vue grille" className={"grid h-7 w-7 place-items-center rounded-full transition-colors " + (view === "grid" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}><Grid2x2 className="h-3.5 w-3.5" /></button>
            <button onClick={() => setView("list")} aria-label="Vue liste" className={"grid h-7 w-7 place-items-center rounded-full transition-colors " + (view === "list" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}><List className="h-3.5 w-3.5" /></button>
          </div>
          <button className="hidden items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground sm:inline-flex"><Send className="h-3.5 w-3.5" /> Transférer</button>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-[0_8px_24px_-12px_oklch(0.78_0.18_150/0.6)] transition-opacity hover:opacity-90"><UserPlus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Ajouter patient</span></button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {PATIENTS.map((p) => (
            <Card key={p.id} p={p} active={selected.id === p.id} onSelect={() => setSelected(p)} />
          ))}
        </div>
        <DetailPanel p={selected} />
      </div>

      <div className="card-surface flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3">
        <div className="flex shrink-0 items-center gap-2 text-sm">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-destructive/20 text-xs font-semibold text-destructive">!</span>
          <span>Patients avec alertes</span>
          <span className="text-muted-foreground">({alerts.length})</span>
          <button onClick={() => setMuted((m) => !m)} aria-label={muted ? "Réactiver les alertes" : "Couper les alertes"} className="ml-1 grid h-7 w-7 place-items-center rounded-full bg-white/5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground">
            {muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2.5 pr-2">
          {alerts.map((a) => (
            <button key={a.id} onClick={() => setSelected(a)} className={"relative rounded-lg border bg-black/40 px-3 py-1.5 text-left text-[10px] font-mono transition-all hover:-translate-y-0.5 hover:border-destructive/50 " + (selected.id === a.id ? "border-primary/60 ring-1 ring-primary/40" : "border-white/10")}>
              <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground">{a.alerts}</span>
              <div className="text-foreground">{a.id}</div>
              <div className="text-muted-foreground">{a.ref}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

